import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import Sidebar from '../Dashboard/Sidebar';
import Loader from '../layouts/Loader';
import MetaData from '../layouts/MetaData';
import DeleteConfirmModal from '../layouts/DeleteConfirmModal';
import formatDate from '../../utils/formatDate';
import {
    getHospitalData,
    addHospitalContact,
    updateHospitalContact,
    deleteHospitalContact,
    addHospitalDocument,
    deleteHospitalDocument,
    addHospitalCategory,
    deleteHospitalCategory
} from '../../actions/hospitalActions';
import {
    clearHospitalError,
    clearContactCreated,
    clearContactUpdated,
    clearContactDeleted,
    clearDocCreated,
    clearDocDeleted,
    clearCategoryCreated,
    clearCategoryDeleted
} from '../../slices/hospitalSlice';

// Sensible fallbacks if DB has not yet populated
const DEFAULT_CONTACT_CATEGORIES = [
    'Pharmacy & Medicine',
    'Lab & Diagnostics',
    'Hospital Equipment & Surgicals',
    'Grocery & Diet Supplies',
    'Laundry & Linen',
    'Electrician & Maintenance',
    'Plumbing & Sanitization',
    'Waste Management & Bio-waste',
    'IT & Technical Support',
    'Legal & Compliance',
    'Visiting Doctor / Consultant',
    'Emergency & Ambulance',
    'Other'
];

const DEFAULT_DOC_CATEGORIES = [
    'Clinical Establishment License',
    'Pollution Control & Bio-Waste',
    'Fire Safety NOC',
    'Trade License / Local Body',
    'Tax & GST Registration',
    'Insurance & Empanelment',
    'Doctor Registration & Certs',
    'Hospital Accreditation & ISO',
    'Equipment AMC / Warranties',
    'Staff Verification & Agreements',
    'Building & Property Papers',
    'Other'
];

const formatBytes = (bytes) => {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

export default function HospitalDetails() {
    const dispatch = useDispatch();

    const { user } = useSelector(state => state.authState);
    const canManage = user?.role === 'admin' || user?.role === 'manager' || user?.role === 'master';

    const {
        loading,
        contacts = [],
        documents = [],
        categories = { contacts: [], documents: [] },
        error,
        isContactCreated,
        isContactUpdated,
        isContactDeleted,
        isDocCreated,
        isDocDeleted,
        isCategoryCreated,
        isCategoryDeleted
    } = useSelector(state => state.hospitalState);

    // Dynamic categories from backend
    const contactCategories = useMemo(() => {
        const list = categories?.contacts || [];
        if (list.length > 0) return list;
        return DEFAULT_CONTACT_CATEGORIES.map((name, i) => ({ id: i + 1, name }));
    }, [categories]);

    const documentCategories = useMemo(() => {
        const list = categories?.documents || [];
        if (list.length > 0) return list;
        return DEFAULT_DOC_CATEGORIES.map((name, i) => ({ id: i + 1, name }));
    }, [categories]);

    // Filter & Search states
    const [contactSearch, setContactSearch] = useState('');
    const [contactCategoryFilter, setContactCategoryFilter] = useState('ALL');

    const [docSearch, setDocSearch] = useState('');
    const [docCategoryFilter, setDocCategoryFilter] = useState('ALL');

    // Modals
    const [showContactModal, setShowContactModal] = useState(false);
    const [editingContact, setEditingContact] = useState(null);
    const [contactForm, setContactForm] = useState({
        name: '',
        category: '',
        contactPerson: '',
        phone: '',
        altPhone: '',
        email: '',
        address: '',
        notes: ''
    });

    const [showDocModal, setShowDocModal] = useState(false);
    const [docForm, setDocForm] = useState({
        title: '',
        category: '',
        documentNumber: '',
        issueDate: '',
        expiryDate: '',
        notes: '',
        file: null
    });
    const [fileInputKey, setFileInputKey] = useState(Date.now());

    // Preview Modal for Document
    const [previewDoc, setPreviewDoc] = useState(null);

    // Category Management Modal
    const [showCategoryModal, setShowCategoryModal] = useState(false);
    const [activeCategoryTab, setActiveCategoryTab] = useState('contact'); // 'contact' or 'document'
    const [newCategoryName, setNewCategoryName] = useState('');

    // Delete Modal
    const [deleteModal, setDeleteModal] = useState({
        show: false,
        type: null, // 'contact', 'document', or 'category'
        id: null,
        name: ''
    });

    // Submitting state
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Load initial data
    useEffect(() => {
        dispatch(getHospitalData());
    }, [dispatch]);

    // Handle feedback toasts
    useEffect(() => {
        if (error) {
            toast.error(error);
            dispatch(clearHospitalError());
            setIsSubmitting(false);
        }

        if (isContactCreated) {
            toast.success('Contact added successfully');
            dispatch(clearContactCreated());
            setShowContactModal(false);
            resetContactForm();
            setIsSubmitting(false);
        }

        if (isContactUpdated) {
            toast.success('Contact updated successfully');
            dispatch(clearContactUpdated());
            setShowContactModal(false);
            resetContactForm();
            setIsSubmitting(false);
        }

        if (isContactDeleted) {
            toast.success('Contact removed');
            dispatch(clearContactDeleted());
            setDeleteModal({ show: false, type: null, id: null, name: '' });
            setIsSubmitting(false);
        }

        if (isDocCreated) {
            toast.success('Document uploaded successfully');
            dispatch(clearDocCreated());
            setShowDocModal(false);
            resetDocForm();
            setIsSubmitting(false);
        }

        if (isDocDeleted) {
            toast.success('Document deleted');
            dispatch(clearDocDeleted());
            setDeleteModal({ show: false, type: null, id: null, name: '' });
            setIsSubmitting(false);
        }

        if (isCategoryCreated) {
            toast.success('Category added');
            dispatch(clearCategoryCreated());
            setNewCategoryName('');
            setIsSubmitting(false);
        }

        if (isCategoryDeleted) {
            toast.success('Category removed');
            dispatch(clearCategoryDeleted());
            setDeleteModal({ show: false, type: null, id: null, name: '' });
            setIsSubmitting(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [error, isContactCreated, isContactUpdated, isContactDeleted, isDocCreated, isDocDeleted, isCategoryCreated, isCategoryDeleted, dispatch]);

    const resetContactForm = () => {
        setEditingContact(null);
        setContactForm({
            name: '',
            category: contactCategories[0]?.name || 'Other',
            contactPerson: '',
            phone: '',
            altPhone: '',
            email: '',
            address: '',
            notes: ''
        });
    };

    const resetDocForm = () => {
        setDocForm({
            title: '',
            category: documentCategories[0]?.name || 'General',
            documentNumber: '',
            issueDate: '',
            expiryDate: '',
            notes: '',
            file: null
        });
        setFileInputKey(Date.now());
    };

    const openAddContactModal = () => {
        resetContactForm();
        setShowContactModal(true);
    };

    const openEditContactModal = (contact) => {
        setEditingContact(contact);
        setContactForm({
            name: contact.name || '',
            category: contact.category || contactCategories[0]?.name || 'Other',
            contactPerson: contact.contactPerson || '',
            phone: contact.phone || '',
            altPhone: contact.altPhone || '',
            email: contact.email || '',
            address: contact.address || '',
            notes: contact.notes || ''
        });
        setShowContactModal(true);
    };

    const handleContactSubmit = (e) => {
        e.preventDefault();
        if (!contactForm.name.trim()) {
            toast.error('Shop / Contact name is required');
            return;
        }
        if (!contactForm.phone.trim()) {
            toast.error('Phone number is required');
            return;
        }

        setIsSubmitting(true);
        if (editingContact) {
            dispatch(updateHospitalContact(editingContact.id || editingContact._id, contactForm));
        } else {
            dispatch(addHospitalContact(contactForm));
        }
    };

    const handleDocSubmit = (e) => {
        e.preventDefault();
        if (!docForm.title.trim()) {
            toast.error('Document title is required');
            return;
        }
        if (!docForm.file) {
            toast.error('Please choose a file to upload');
            return;
        }

        const formData = new FormData();
        formData.append('title', docForm.title.trim());
        formData.append('category', docForm.category || documentCategories[0]?.name || 'General');
        if (docForm.documentNumber) formData.append('documentNumber', docForm.documentNumber.trim());
        if (docForm.issueDate) formData.append('issueDate', docForm.issueDate);
        if (docForm.expiryDate) formData.append('expiryDate', docForm.expiryDate);
        if (docForm.notes) formData.append('notes', docForm.notes.trim());
        formData.append('file', docForm.file);

        setIsSubmitting(true);
        dispatch(addHospitalDocument(formData));
    };

    const handleAddCategorySubmit = (e) => {
        e.preventDefault();
        if (!newCategoryName.trim()) {
            toast.error('Please enter a category name');
            return;
        }
        setIsSubmitting(true);
        dispatch(addHospitalCategory({
            name: newCategoryName.trim(),
            type: activeCategoryTab
        }));
    };

    const confirmDelete = () => {
        setIsSubmitting(true);
        if (deleteModal.type === 'contact') {
            dispatch(deleteHospitalContact(deleteModal.id));
        } else if (deleteModal.type === 'document') {
            dispatch(deleteHospitalDocument(deleteModal.id));
        } else if (deleteModal.type === 'category') {
            dispatch(deleteHospitalCategory(deleteModal.id));
        }
    };

    // Filtered contacts
    const filteredContacts = useMemo(() => {
        return (contacts || []).filter(c => {
            const matchesCat = contactCategoryFilter === 'ALL' || c.category === contactCategoryFilter;
            if (!matchesCat) return false;

            if (!contactSearch.trim()) return true;
            const q = contactSearch.toLowerCase();
            return (
                (c.name && c.name.toLowerCase().includes(q)) ||
                (c.contactPerson && c.contactPerson.toLowerCase().includes(q)) ||
                (c.phone && c.phone.toLowerCase().includes(q)) ||
                (c.altPhone && c.altPhone.toLowerCase().includes(q)) ||
                (c.category && c.category.toLowerCase().includes(q)) ||
                (c.address && c.address.toLowerCase().includes(q)) ||
                (c.notes && c.notes.toLowerCase().includes(q))
            );
        });
    }, [contacts, contactSearch, contactCategoryFilter]);

    // Filtered documents
    const filteredDocuments = useMemo(() => {
        return (documents || []).filter(d => {
            const matchesCat = docCategoryFilter === 'ALL' || d.category === docCategoryFilter;
            if (!matchesCat) return false;

            if (!docSearch.trim()) return true;
            const q = docSearch.toLowerCase();
            const docNum = d.documentNumber || d.docNumber || '';
            const fileName = d.fileOriginalName || d.file || '';
            return (
                (d.title && d.title.toLowerCase().includes(q)) ||
                (d.category && d.category.toLowerCase().includes(q)) ||
                (docNum && docNum.toLowerCase().includes(q)) ||
                (fileName && fileName.toLowerCase().includes(q)) ||
                (d.notes && d.notes.toLowerCase().includes(q))
            );
        });
    }, [documents, docSearch, docCategoryFilter]);

    // Document expiry status helper
    const getExpiryBadge = (expiryDate) => {
        if (!expiryDate) {
            return <span className="badge bg-secondary text-white" style={{ fontSize: '0.72rem' }}>No Expiry</span>;
        }
        const exp = new Date(expiryDate);
        const now = new Date();
        const diffDays = Math.ceil((exp - now) / (1000 * 60 * 60 * 24));

        if (diffDays < 0) {
            return (
                <span className="badge bg-danger text-white" style={{ fontSize: '0.72rem' }} title={`Expired on ${formatDate(expiryDate)}`}>
                    <i className="fa fa-exclamation-circle me-1"></i>Expired ({formatDate(expiryDate)})
                </span>
            );
        } else if (diffDays <= 30) {
            return (
                <span className="badge bg-warning text-dark" style={{ fontSize: '0.72rem' }} title={`Expires in ${diffDays} days`}>
                    <i className="fa fa-clock me-1"></i>Expiring Soon ({diffDays}d)
                </span>
            );
        } else {
            return (
                <span className="badge bg-success text-white" style={{ fontSize: '0.72rem' }} title={`Valid till ${formatDate(expiryDate)}`}>
                    <i className="fa fa-check-circle me-1"></i>Valid till {formatDate(expiryDate)}
                </span>
            );
        }
    };

    // Calculate document stats
    const docStats = useMemo(() => {
        let expired = 0;
        let expiringSoon = 0;
        const now = new Date();
        (documents || []).forEach(d => {
            if (d.expiryDate) {
                const diffDays = Math.ceil((new Date(d.expiryDate) - now) / (1000 * 60 * 60 * 24));
                if (diffDays < 0) expired++;
                else if (diffDays <= 30) expiringSoon++;
            }
        });
        return { expired, expiringSoon };
    }, [documents]);

    const cleanPhoneNumber = (p) => {
        if (!p) return '';
        return p.replace(/[^0-9]/g, '');
    };

    const isPdfFile = (url = '', mime = '') => {
        return (url && url.toLowerCase().endsWith('.pdf')) || (mime && mime.includes('pdf'));
    };

    return (
        <div className="row" style={{ margin: 0 }}>
            <MetaData title={'Hospital Directory & Documents - Green Naturopathy'} />

            <div className="col-12 col-md-2 p-0">
                <Sidebar />
            </div>

            <div className="col-12 col-md-10 py-4 px-3 px-md-4" style={{ backgroundColor: 'var(--bg-main, #f8fafc)', minHeight: '100vh' }}>
                {loading && !contacts.length && !documents.length ? (
                    <Loader />
                ) : (
                    <>
                        {/* ── TOP HEADER & SUMMARY BANNER ── */}
                        <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '14px', background: 'linear-gradient(135deg, #064e3b 0%, #0d9488 100%)', color: '#fff' }}>
                            <div className="card-body p-4">
                                <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
                                    <div>
                                        <div className="d-flex align-items-center gap-2 mb-1">
                                            <span style={{ fontSize: '1.6rem' }}>🏥</span>
                                            <h1 className="h3 mb-0 fw-bold text-white">Hospital Info & Documents</h1>
                                        </div>
                                        <p className="mb-0 text-white-50" style={{ fontSize: '0.9rem' }}>
                                            Essential directory of vendor shops, partner contacts, and official hospital regulatory documents & licenses.
                                        </p>
                                    </div>
                                    <div className="d-flex flex-wrap gap-2">
                                        {canManage && (
                                            <>
                                                <button
                                                    className="btn btn-light fw-bold d-flex align-items-center gap-2 shadow-sm"
                                                    style={{ color: '#065f46', borderRadius: '10px' }}
                                                    onClick={openAddContactModal}
                                                >
                                                    <i className="fa fa-plus-circle"></i>
                                                    <span>Add Contact / Shop</span>
                                                </button>
                                                <button
                                                    className="btn btn-outline-light fw-bold d-flex align-items-center gap-2 shadow-sm"
                                                    style={{ borderRadius: '10px' }}
                                                    onClick={() => { resetDocForm(); setShowDocModal(true); }}
                                                >
                                                    <i className="fa fa-file-upload"></i>
                                                    <span>Upload Document</span>
                                                </button>
                                                <button
                                                    className="btn btn-outline-light fw-semibold d-flex align-items-center gap-2 shadow-sm"
                                                    style={{ borderRadius: '10px', backgroundColor: 'rgba(255,255,255,0.15)' }}
                                                    onClick={() => setShowCategoryModal(true)}
                                                    title="Add or remove categories for contacts and documents"
                                                >
                                                    <i className="fa fa-tags"></i>
                                                    <span>Manage Categories</span>
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>

                                {/* Quick Stats Row */}
                                <div className="row g-2 mt-3 pt-3 border-top border-white border-opacity-25">
                                    <div className="col-6 col-sm-3">
                                        <div className="p-2 rounded" style={{ background: 'rgba(255, 255, 255, 0.12)' }}>
                                            <small className="text-white-50 d-block" style={{ fontSize: '0.75rem' }}>Total Contacts</small>
                                            <span className="h5 fw-bold mb-0 text-white">{contacts.length}</span>
                                        </div>
                                    </div>
                                    <div className="col-6 col-sm-3">
                                        <div className="p-2 rounded" style={{ background: 'rgba(255, 255, 255, 0.12)' }}>
                                            <small className="text-white-50 d-block" style={{ fontSize: '0.75rem' }}>Total Documents</small>
                                            <span className="h5 fw-bold mb-0 text-white">{documents.length}</span>
                                        </div>
                                    </div>
                                    <div className="col-6 col-sm-3">
                                        <div className="p-2 rounded" style={{ background: 'rgba(255, 255, 255, 0.12)' }}>
                                            <small className="text-white-50 d-block" style={{ fontSize: '0.75rem' }}>Expiring Soon (30d)</small>
                                            <span className={`h5 fw-bold mb-0 ${docStats.expiringSoon > 0 ? 'text-warning' : 'text-white'}`}>
                                                {docStats.expiringSoon}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="col-6 col-sm-3">
                                        <div className="p-2 rounded" style={{ background: 'rgba(255, 255, 255, 0.12)' }}>
                                            <small className="text-white-50 d-block" style={{ fontSize: '0.75rem' }}>Expired Documents</small>
                                            <span className={`h5 fw-bold mb-0 ${docStats.expired > 0 ? 'text-danger' : 'text-white'}`}>
                                                {docStats.expired}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* ══════════════════════════════════════════════════════════════ */}
                        {/* ── SECTION 1: CONTACTS & SHOPS DIRECTORY ── */}
                        {/* ══════════════════════════════════════════════════════════════ */}
                        <div className="card border-0 shadow-sm mb-5" style={{ borderRadius: '14px' }}>
                            <div className="card-header bg-white border-0 pt-4 px-4 pb-2">
                                <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
                                    <div>
                                        <h2 className="h5 fw-bold mb-1 text-dark d-flex align-items-center gap-2">
                                            <i className="fa fa-address-book" style={{ color: 'var(--primary, #0d9488)' }}></i>
                                            Shops & Contact Directory
                                            <span className="badge rounded-pill bg-light text-muted border ms-1" style={{ fontSize: '0.75rem' }}>
                                                {filteredContacts.length}
                                            </span>
                                        </h2>
                                        <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>
                                            Pharmacy vendors, suppliers, electricians, diagnostics, emergency numbers, and maintenance
                                        </p>
                                    </div>

                                    {/* Search Bar & Manage Categories shortcut */}
                                    <div className="d-flex align-items-center gap-2">
                                        <div className="position-relative" style={{ minWidth: '240px' }}>
                                            <i className="fa fa-search position-absolute top-50 translate-middle-y text-muted" style={{ left: '12px' }}></i>
                                            <input
                                                type="text"
                                                className="form-control form-control-sm ps-4 pe-4"
                                                placeholder="Search shop, name, phone..."
                                                value={contactSearch}
                                                onChange={(e) => setContactSearch(e.target.value)}
                                                style={{ borderRadius: '20px' }}
                                            />
                                            {contactSearch && (
                                                <button
                                                    className="btn btn-sm btn-link position-absolute top-50 translate-middle-y text-muted p-0 text-decoration-none"
                                                    style={{ right: '12px', fontSize: '0.75rem' }}
                                                    onClick={() => setContactSearch('')}
                                                >
                                                    ✕
                                                </button>
                                            )}
                                        </div>
                                        {canManage && (
                                            <button
                                                className="btn btn-sm btn-outline-secondary"
                                                style={{ borderRadius: '20px', whiteSpace: 'nowrap' }}
                                                onClick={() => { setActiveCategoryTab('contact'); setShowCategoryModal(true); }}
                                                title="Edit contact categories"
                                            >
                                                <i className="fa fa-cog me-1"></i> Categories
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {/* Dynamic Category Filters */}
                                <div className="d-flex flex-wrap gap-1 mt-3 pb-1" style={{ overflowX: 'auto' }}>
                                    <button
                                        type="button"
                                        className={`btn btn-sm ${contactCategoryFilter === 'ALL' ? 'btn-success text-white' : 'btn-outline-secondary'}`}
                                        style={{ borderRadius: '20px', fontSize: '0.78rem', padding: '0.2rem 0.75rem' }}
                                        onClick={() => setContactCategoryFilter('ALL')}
                                    >
                                        All ({contacts.length})
                                    </button>
                                    {contactCategories.map(cat => {
                                        const catName = typeof cat === 'string' ? cat : cat.name;
                                        const count = contacts.filter(c => c.category === catName).length;
                                        if (count === 0 && contactCategoryFilter !== catName) return null;
                                        return (
                                            <button
                                                key={cat.id || catName}
                                                type="button"
                                                className={`btn btn-sm ${contactCategoryFilter === catName ? 'btn-success text-white' : 'btn-outline-secondary'}`}
                                                style={{ borderRadius: '20px', fontSize: '0.78rem', padding: '0.2rem 0.75rem' }}
                                                onClick={() => setContactCategoryFilter(catName)}
                                            >
                                                {catName} {count > 0 ? `(${count})` : ''}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            <div className="card-body p-4 pt-2">
                                {filteredContacts.length === 0 ? (
                                    <div className="text-center py-5 text-muted">
                                        <i className="fa fa-phone-slash fa-3x mb-3 text-secondary opacity-50"></i>
                                        <p className="mb-2 fw-semibold">No contacts found</p>
                                        <small>
                                            {contacts.length === 0
                                                ? 'No contacts have been added yet. Click "Add Contact / Shop" above to add the first one.'
                                                : 'No contacts match your current search/filter.'}
                                        </small>
                                    </div>
                                ) : (
                                    <div className="row g-3">
                                        {filteredContacts.map(c => {
                                            const cleanPhone = cleanPhoneNumber(c.phone);
                                            const cleanAlt = cleanPhoneNumber(c.altPhone);
                                            const waPhone = cleanPhone.slice(-10);

                                            return (
                                                <div key={c.id || c._id} className="col-12 col-md-6 col-lg-4">
                                                    <div className="card h-100 border border-1 shadow-sm" style={{ borderRadius: '12px', transition: 'transform 0.15s, box-shadow 0.15s' }}>
                                                        <div className="card-body p-3 d-flex flex-column justify-content-between">
                                                            <div>
                                                                {/* Header: Name + Category badge + action buttons */}
                                                                <div className="d-flex justify-content-between align-items-start mb-2">
                                                                    <div style={{ flex: 1, minWidth: 0, paddingRight: '8px' }}>
                                                                        <h3 className="h6 fw-bold mb-1 text-truncate text-dark" title={c.name}>
                                                                            {c.name}
                                                                        </h3>
                                                                        <span
                                                                            className="badge"
                                                                            style={{
                                                                                fontSize: '0.7rem',
                                                                                backgroundColor: 'rgba(13, 148, 136, 0.12)',
                                                                                color: '#0f766e',
                                                                                border: '1px solid rgba(13, 148, 136, 0.25)'
                                                                            }}
                                                                        >
                                                                            {c.category || 'Vendor'}
                                                                        </span>
                                                                    </div>
                                                                    {canManage && (
                                                                        <div className="d-flex gap-1">
                                                                            <button
                                                                                className="btn btn-sm btn-light p-1 px-2 text-secondary"
                                                                                title="Edit contact"
                                                                                onClick={() => openEditContactModal(c)}
                                                                            >
                                                                                <i className="fa fa-edit"></i>
                                                                            </button>
                                                                            <button
                                                                                className="btn btn-sm btn-light p-1 px-2 text-danger"
                                                                                title="Delete contact"
                                                                                onClick={() => setDeleteModal({
                                                                                    show: true,
                                                                                    type: 'contact',
                                                                                    id: c.id || c._id,
                                                                                    name: c.name
                                                                                })}
                                                                            >
                                                                                <i className="fa fa-trash"></i>
                                                                            </button>
                                                                        </div>
                                                                    )}
                                                                </div>

                                                                {/* Details */}
                                                                <div className="text-secondary small mt-2">
                                                                    {c.contactPerson && (
                                                                        <div className="mb-1 d-flex align-items-center gap-2">
                                                                            <i className="fa fa-user text-muted" style={{ width: '16px' }}></i>
                                                                            <span className="fw-semibold text-dark">{c.contactPerson}</span>
                                                                        </div>
                                                                    )}
                                                                    <div className="mb-1 d-flex align-items-center gap-2">
                                                                        <i className="fa fa-phone text-muted" style={{ width: '16px' }}></i>
                                                                        <span className="fw-bold text-dark">{c.phone}</span>
                                                                        {c.altPhone && (
                                                                            <span className="text-muted" style={{ fontSize: '0.8rem' }}>/ {c.altPhone}</span>
                                                                        )}
                                                                    </div>
                                                                    {c.email && (
                                                                        <div className="mb-1 d-flex align-items-center gap-2 text-truncate">
                                                                            <i className="fa fa-envelope text-muted" style={{ width: '16px' }}></i>
                                                                            <a href={`mailto:${c.email}`} className="text-decoration-none text-secondary text-truncate">
                                                                                {c.email}
                                                                            </a>
                                                                        </div>
                                                                    )}
                                                                    {c.address && (
                                                                        <div className="mb-1 d-flex align-items-start gap-2">
                                                                            <i className="fa fa-map-marker-alt text-muted mt-1" style={{ width: '16px' }}></i>
                                                                            <span style={{ fontSize: '0.8rem', lineHeight: '1.25' }}>{c.address}</span>
                                                                        </div>
                                                                    )}
                                                                    {c.notes && (
                                                                        <div className="mt-2 p-2 bg-light rounded text-muted" style={{ fontSize: '0.78rem' }}>
                                                                            <i className="fa fa-info-circle me-1 text-primary"></i>
                                                                            {c.notes}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>

                                                            {/* Quick Action Footer */}
                                                            <div className="d-flex gap-2 mt-3 pt-2 border-top">
                                                                <a
                                                                    href={`tel:${cleanPhone}`}
                                                                    className="btn btn-sm btn-outline-success flex-fill d-flex align-items-center justify-content-center gap-1"
                                                                    style={{ borderRadius: '8px', fontSize: '0.8rem' }}
                                                                >
                                                                    <i className="fa fa-phone-alt"></i> Call
                                                                </a>
                                                                {waPhone.length === 10 && (
                                                                    <a
                                                                        href={`https://wa.me/91${waPhone}?text=${encodeURIComponent('Hello, contacting from Green Naturopathy Hospital.')}`}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        className="btn btn-sm btn-success flex-fill d-flex align-items-center justify-content-center gap-1"
                                                                        style={{ borderRadius: '8px', fontSize: '0.8rem', backgroundColor: '#25D366', borderColor: '#25D366' }}
                                                                    >
                                                                        <i className="fab fa-whatsapp"></i> WhatsApp
                                                                    </a>
                                                                )}
                                                                {cleanAlt && (
                                                                    <a
                                                                        href={`tel:${cleanAlt}`}
                                                                        className="btn btn-sm btn-outline-secondary p-1 px-2"
                                                                        style={{ borderRadius: '8px', fontSize: '0.8rem' }}
                                                                        title={`Call Alt Phone: ${c.altPhone}`}
                                                                    >
                                                                        <i className="fa fa-phone"></i> 2
                                                                    </a>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* ══════════════════════════════════════════════════════════════ */}
                        {/* ── SECTION 2: HOSPITAL DOCUMENTS & CERTIFICATES ── */}
                        {/* ══════════════════════════════════════════════════════════════ */}
                        <div className="card border-0 shadow-sm" style={{ borderRadius: '14px' }}>
                            <div className="card-header bg-white border-0 pt-4 px-4 pb-2">
                                <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
                                    <div>
                                        <h2 className="h5 fw-bold mb-1 text-dark d-flex align-items-center gap-2">
                                            <i className="fa fa-file-contract" style={{ color: 'var(--primary, #0d9488)' }}></i>
                                            Official Documents & Certificates
                                            <span className="badge rounded-pill bg-light text-muted border ms-1" style={{ fontSize: '0.75rem' }}>
                                                {filteredDocuments.length}
                                            </span>
                                        </h2>
                                        <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>
                                            Registration certificates, licenses, pollution & fire safety NOCs, insurance empanelments, and clinic records
                                        </p>
                                    </div>

                                    {/* Search Bar & Manage Categories shortcut */}
                                    <div className="d-flex align-items-center gap-2">
                                        <div className="position-relative" style={{ minWidth: '240px' }}>
                                            <i className="fa fa-search position-absolute top-50 translate-middle-y text-muted" style={{ left: '12px' }}></i>
                                            <input
                                                type="text"
                                                className="form-control form-control-sm ps-4 pe-4"
                                                placeholder="Search document title, number..."
                                                value={docSearch}
                                                onChange={(e) => setDocSearch(e.target.value)}
                                                style={{ borderRadius: '20px' }}
                                            />
                                            {docSearch && (
                                                <button
                                                    className="btn btn-sm btn-link position-absolute top-50 translate-middle-y text-muted p-0 text-decoration-none"
                                                    style={{ right: '12px', fontSize: '0.75rem' }}
                                                    onClick={() => setDocSearch('')}
                                                >
                                                    ✕
                                                </button>
                                            )}
                                        </div>
                                        {canManage && (
                                            <button
                                                className="btn btn-sm btn-outline-secondary"
                                                style={{ borderRadius: '20px', whiteSpace: 'nowrap' }}
                                                onClick={() => { setActiveCategoryTab('document'); setShowCategoryModal(true); }}
                                                title="Edit document categories"
                                            >
                                                <i className="fa fa-cog me-1"></i> Categories
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {/* Dynamic Category Filters */}
                                <div className="d-flex flex-wrap gap-1 mt-3 pb-1" style={{ overflowX: 'auto' }}>
                                    <button
                                        type="button"
                                        className={`btn btn-sm ${docCategoryFilter === 'ALL' ? 'btn-success text-white' : 'btn-outline-secondary'}`}
                                        style={{ borderRadius: '20px', fontSize: '0.78rem', padding: '0.2rem 0.75rem' }}
                                        onClick={() => setDocCategoryFilter('ALL')}
                                    >
                                        All ({documents.length})
                                    </button>
                                    {documentCategories.map(cat => {
                                        const catName = typeof cat === 'string' ? cat : cat.name;
                                        const count = documents.filter(d => d.category === catName).length;
                                        if (count === 0 && docCategoryFilter !== catName) return null;
                                        return (
                                            <button
                                                key={cat.id || catName}
                                                type="button"
                                                className={`btn btn-sm ${docCategoryFilter === catName ? 'btn-success text-white' : 'btn-outline-secondary'}`}
                                                style={{ borderRadius: '20px', fontSize: '0.78rem', padding: '0.2rem 0.75rem' }}
                                                onClick={() => setDocCategoryFilter(catName)}
                                            >
                                                {catName} {count > 0 ? `(${count})` : ''}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            <div className="card-body p-4 pt-2">
                                {filteredDocuments.length === 0 ? (
                                    <div className="text-center py-5 text-muted">
                                        <i className="fa fa-folder-open fa-3x mb-3 text-secondary opacity-50"></i>
                                        <p className="mb-2 fw-semibold">No documents found</p>
                                        <small>
                                            {documents.length === 0
                                                ? 'No documents have been uploaded yet. Click "Upload Document" to store licenses and certificates.'
                                                : 'No documents match your current search/filter.'}
                                        </small>
                                    </div>
                                ) : (
                                    <div className="table-responsive">
                                        <table className="table table-hover align-middle mb-0">
                                            <thead className="table-light">
                                                <tr style={{ fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                                    <th>Document</th>
                                                    <th>Category</th>
                                                    <th>Doc / Reg #</th>
                                                    <th>Issue / Expiry</th>
                                                    <th>File</th>
                                                    <th className="text-end">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredDocuments.map(doc => {
                                                    const fileUrl = doc.fileUrl || doc.file || '';
                                                    const docNumber = doc.documentNumber || doc.docNumber || '';
                                                    const isPdf = isPdfFile(fileUrl, doc.fileType);
                                                    return (
                                                        <tr key={doc.id || doc._id}>
                                                            <td>
                                                                <div className="d-flex align-items-center gap-2">
                                                                    <div
                                                                        className="rounded p-2 text-center"
                                                                        style={{
                                                                            width: '38px',
                                                                            height: '38px',
                                                                            backgroundColor: isPdf ? 'rgba(239, 68, 68, 0.12)' : 'rgba(59, 130, 246, 0.12)',
                                                                            color: isPdf ? '#dc2626' : '#2563eb'
                                                                        }}
                                                                    >
                                                                        <i className={isPdf ? 'fa fa-file-pdf' : 'fa fa-file-image'}></i>
                                                                    </div>
                                                                    <div>
                                                                        <div className="fw-bold text-dark">{doc.title}</div>
                                                                        {doc.notes && (
                                                                            <small className="text-muted d-block text-truncate" style={{ maxWidth: '280px', fontSize: '0.78rem' }}>
                                                                                {doc.notes}
                                                                            </small>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <span
                                                                    className="badge"
                                                                    style={{
                                                                        fontSize: '0.72rem',
                                                                        backgroundColor: 'rgba(13, 148, 136, 0.12)',
                                                                        color: '#0f766e',
                                                                        border: '1px solid rgba(13, 148, 136, 0.25)'
                                                                    }}
                                                                >
                                                                    {doc.category || 'General'}
                                                                </span>
                                                            </td>
                                                            <td>
                                                                <span className="fw-semibold text-secondary" style={{ fontSize: '0.85rem' }}>
                                                                    {docNumber || '—'}
                                                                </span>
                                                            </td>
                                                            <td>
                                                                <div className="d-flex flex-column gap-1">
                                                                    {getExpiryBadge(doc.expiryDate)}
                                                                    {doc.issueDate && (
                                                                        <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                                                                            Issued: {formatDate(doc.issueDate)}
                                                                        </small>
                                                                    )}
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <div style={{ fontSize: '0.8rem' }}>
                                                                    <span className="text-muted">{formatBytes(doc.fileSize)}</span>
                                                                </div>
                                                            </td>
                                                            <td className="text-end">
                                                                <div className="btn-group btn-group-sm">
                                                                    {/* In-Browser Preview */}
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-outline-primary"
                                                                        title="Preview Document"
                                                                        onClick={() => setPreviewDoc(doc)}
                                                                    >
                                                                        <i className="fa fa-eye me-1"></i> Preview
                                                                    </button>

                                                                    {/* Direct Download */}
                                                                    <a
                                                                        href={fileUrl}
                                                                        download={doc.fileOriginalName || doc.title}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        className="btn btn-outline-secondary"
                                                                        title="Download Document"
                                                                    >
                                                                        <i className="fa fa-download"></i>
                                                                    </a>

                                                                    {/* Delete (if canManage) */}
                                                                    {canManage && (
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-outline-danger"
                                                                            title="Delete Document"
                                                                            onClick={() => setDeleteModal({
                                                                                show: true,
                                                                                type: 'document',
                                                                                id: doc.id || doc._id,
                                                                                name: doc.title
                                                                            })}
                                                                        >
                                                                            <i className="fa fa-trash"></i>
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* ══════════════════════════════════════════════════════════════ */}
                        {/* ── MODAL: ADD / EDIT CONTACT ── */}
                        {/* ══════════════════════════════════════════════════════════════ */}
                        {showContactModal && (
                            <>
                                <div className="modal-backdrop fade show" style={{ zIndex: 1050 }}></div>
                                <div className="modal fade show d-block" tabIndex="-1" style={{ zIndex: 1055, background: 'rgba(0,0,0,0.5)' }}>
                                    <div className="modal-dialog modal-dialog-centered modal-lg">
                                        <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '14px' }}>
                                            <div className="modal-header border-0 pb-0">
                                                <h5 className="modal-title fw-bold text-dark d-flex align-items-center gap-2">
                                                    <i className="fa fa-address-book" style={{ color: 'var(--primary, #0d9488)' }}></i>
                                                    {editingContact ? 'Edit Shop / Contact' : 'Add New Shop / Contact'}
                                                </h5>
                                                <button
                                                    type="button"
                                                    className="btn-close"
                                                    onClick={() => !isSubmitting && setShowContactModal(false)}
                                                ></button>
                                            </div>
                                            <form onSubmit={handleContactSubmit}>
                                                <div className="modal-body">
                                                    <div className="row g-3">
                                                        <div className="col-12 col-md-7">
                                                            <label className="form-label fw-semibold small">
                                                                Shop / Vendor / Place Name <span className="text-danger">*</span>
                                                            </label>
                                                            <input
                                                                type="text"
                                                                className="form-control"
                                                                placeholder="e.g. Apollo Pharmacy / City Bio-waste Ltd."
                                                                value={contactForm.name}
                                                                onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                                                                required
                                                            />
                                                        </div>

                                                        <div className="col-12 col-md-5">
                                                            <div className="d-flex justify-content-between align-items-center mb-1">
                                                                <label className="form-label fw-semibold small mb-0">Category</label>
                                                                {canManage && (
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-link btn-sm p-0 text-decoration-none"
                                                                        style={{ fontSize: '0.75rem', color: 'var(--primary, #0d9488)' }}
                                                                        onClick={() => { setActiveCategoryTab('contact'); setShowCategoryModal(true); }}
                                                                    >
                                                                        + Manage Categories
                                                                    </button>
                                                                )}
                                                            </div>
                                                            <select
                                                                className="form-select"
                                                                value={contactForm.category || (contactCategories[0]?.name || 'Other')}
                                                                onChange={(e) => setContactForm({ ...contactForm, category: e.target.value })}
                                                            >
                                                                {contactCategories.map(cat => {
                                                                    const catName = typeof cat === 'string' ? cat : cat.name;
                                                                    return <option key={cat.id || catName} value={catName}>{catName}</option>;
                                                                })}
                                                            </select>
                                                        </div>

                                                        <div className="col-12 col-md-6">
                                                            <label className="form-label fw-semibold small">Contact Person / In-charge</label>
                                                            <input
                                                                type="text"
                                                                className="form-control"
                                                                placeholder="e.g. Mr. Ramesh (Manager)"
                                                                value={contactForm.contactPerson}
                                                                onChange={(e) => setContactForm({ ...contactForm, contactPerson: e.target.value })}
                                                            />
                                                        </div>

                                                        <div className="col-12 col-md-6">
                                                            <label className="form-label fw-semibold small">
                                                                Phone Number <span className="text-danger">*</span>
                                                            </label>
                                                            <input
                                                                type="text"
                                                                className="form-control"
                                                                placeholder="e.g. 9876543210"
                                                                value={contactForm.phone}
                                                                onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                                                                required
                                                            />
                                                        </div>

                                                        <div className="col-12 col-md-6">
                                                            <label className="form-label fw-semibold small">Alternate Phone / Landline</label>
                                                            <input
                                                                type="text"
                                                                className="form-control"
                                                                placeholder="e.g. 0422-2345678"
                                                                value={contactForm.altPhone}
                                                                onChange={(e) => setContactForm({ ...contactForm, altPhone: e.target.value })}
                                                            />
                                                        </div>

                                                        <div className="col-12 col-md-6">
                                                            <label className="form-label fw-semibold small">Email Address</label>
                                                            <input
                                                                type="email"
                                                                className="form-control"
                                                                placeholder="e.g. contact@vendor.com"
                                                                value={contactForm.email}
                                                                onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                                                            />
                                                        </div>

                                                        <div className="col-12">
                                                            <label className="form-label fw-semibold small">Address / Location</label>
                                                            <textarea
                                                                className="form-control"
                                                                rows="2"
                                                                placeholder="e.g. 45 Trichy Road, Singanallur, Coimbatore - 641005"
                                                                value={contactForm.address}
                                                                onChange={(e) => setContactForm({ ...contactForm, address: e.target.value })}
                                                            ></textarea>
                                                        </div>

                                                        <div className="col-12">
                                                            <label className="form-label fw-semibold small">Notes / Service Details</label>
                                                            <textarea
                                                                className="form-control"
                                                                rows="2"
                                                                placeholder="e.g. Delivers medicines within 2 hours, payment terms net 15 days"
                                                                value={contactForm.notes}
                                                                onChange={(e) => setContactForm({ ...contactForm, notes: e.target.value })}
                                                            ></textarea>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="modal-footer border-0 pt-0">
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-secondary"
                                                        onClick={() => setShowContactModal(false)}
                                                        disabled={isSubmitting}
                                                    >
                                                        Cancel
                                                    </button>
                                                    <button
                                                        type="submit"
                                                        className="btn btn-success fw-semibold"
                                                        disabled={isSubmitting}
                                                    >
                                                        {isSubmitting ? 'Saving...' : (editingContact ? 'Update Contact' : 'Save Contact')}
                                                    </button>
                                                </div>
                                            </form>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}

                        {/* ══════════════════════════════════════════════════════════════ */}
                        {/* ── MODAL: UPLOAD DOCUMENT ── */}
                        {/* ══════════════════════════════════════════════════════════════ */}
                        {showDocModal && (
                            <>
                                <div className="modal-backdrop fade show" style={{ zIndex: 1050 }}></div>
                                <div className="modal fade show d-block" tabIndex="-1" style={{ zIndex: 1055, background: 'rgba(0,0,0,0.5)' }}>
                                    <div className="modal-dialog modal-dialog-centered modal-lg">
                                        <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '14px' }}>
                                            <div className="modal-header border-0 pb-0">
                                                <h5 className="modal-title fw-bold text-dark d-flex align-items-center gap-2">
                                                    <i className="fa fa-file-upload" style={{ color: 'var(--primary, #0d9488)' }}></i>
                                                    Upload Hospital Document / Certificate
                                                </h5>
                                                <button
                                                    type="button"
                                                    className="btn-close"
                                                    onClick={() => !isSubmitting && setShowDocModal(false)}
                                                ></button>
                                            </div>
                                            <form onSubmit={handleDocSubmit}>
                                                <div className="modal-body">
                                                    <div className="row g-3">
                                                        <div className="col-12 col-md-7">
                                                            <label className="form-label fw-semibold small">
                                                                Document Title <span className="text-danger">*</span>
                                                            </label>
                                                            <input
                                                                type="text"
                                                                className="form-control"
                                                                placeholder="e.g. Clinical Establishment Act License 2026"
                                                                value={docForm.title}
                                                                onChange={(e) => setDocForm({ ...docForm, title: e.target.value })}
                                                                required
                                                            />
                                                        </div>

                                                        <div className="col-12 col-md-5">
                                                            <div className="d-flex justify-content-between align-items-center mb-1">
                                                                <label className="form-label fw-semibold small mb-0">Category</label>
                                                                {canManage && (
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-link btn-sm p-0 text-decoration-none"
                                                                        style={{ fontSize: '0.75rem', color: 'var(--primary, #0d9488)' }}
                                                                        onClick={() => { setActiveCategoryTab('document'); setShowCategoryModal(true); }}
                                                                    >
                                                                        + Manage Categories
                                                                    </button>
                                                                )}
                                                            </div>
                                                            <select
                                                                className="form-select"
                                                                value={docForm.category || (documentCategories[0]?.name || 'General')}
                                                                onChange={(e) => setDocForm({ ...docForm, category: e.target.value })}
                                                            >
                                                                {documentCategories.map(cat => {
                                                                    const catName = typeof cat === 'string' ? cat : cat.name;
                                                                    return <option key={cat.id || catName} value={catName}>{catName}</option>;
                                                                })}
                                                            </select>
                                                        </div>

                                                        <div className="col-12 col-md-4">
                                                            <label className="form-label fw-semibold small">Document / License / Reg Number</label>
                                                            <input
                                                                type="text"
                                                                className="form-control"
                                                                placeholder="e.g. TN/CBE/2024/0987"
                                                                value={docForm.documentNumber}
                                                                onChange={(e) => setDocForm({ ...docForm, documentNumber: e.target.value })}
                                                            />
                                                        </div>

                                                        <div className="col-12 col-md-4">
                                                            <label className="form-label fw-semibold small">Issue Date</label>
                                                            <input
                                                                type="date"
                                                                className="form-control"
                                                                value={docForm.issueDate}
                                                                onChange={(e) => setDocForm({ ...docForm, issueDate: e.target.value })}
                                                            />
                                                        </div>

                                                        <div className="col-12 col-md-4">
                                                            <label className="form-label fw-semibold small">
                                                                Expiry Date <small className="text-muted">(if applicable)</small>
                                                            </label>
                                                            <input
                                                                type="date"
                                                                className="form-control"
                                                                value={docForm.expiryDate}
                                                                onChange={(e) => setDocForm({ ...docForm, expiryDate: e.target.value })}
                                                            />
                                                        </div>

                                                        <div className="col-12">
                                                            <label className="form-label fw-semibold small">
                                                                Select File (PDF or Image, max 25MB) <span className="text-danger">*</span>
                                                            </label>
                                                            <input
                                                                key={fileInputKey}
                                                                type="file"
                                                                className="form-control"
                                                                accept=".pdf,image/png,image/jpeg,image/jpg,image/webp"
                                                                onChange={(e) => {
                                                                    if (e.target.files && e.target.files[0]) {
                                                                        setDocForm({ ...docForm, file: e.target.files[0] });
                                                                    }
                                                                }}
                                                                required
                                                            />
                                                            <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                                                                Accepted formats: PDF, PNG, JPG, JPEG, WEBP.
                                                            </small>
                                                        </div>

                                                        <div className="col-12">
                                                            <label className="form-label fw-semibold small">Notes / Remarks</label>
                                                            <textarea
                                                                className="form-control"
                                                                rows="2"
                                                                placeholder="e.g. Original stored in Admin File Rack 3, renewed annually"
                                                                value={docForm.notes}
                                                                onChange={(e) => setDocForm({ ...docForm, notes: e.target.value })}
                                                            ></textarea>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="modal-footer border-0 pt-0">
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-secondary"
                                                        onClick={() => setShowDocModal(false)}
                                                        disabled={isSubmitting}
                                                    >
                                                        Cancel
                                                    </button>
                                                    <button
                                                        type="submit"
                                                        className="btn btn-success fw-semibold"
                                                        disabled={isSubmitting}
                                                    >
                                                        {isSubmitting ? 'Uploading...' : 'Upload Document'}
                                                    </button>
                                                </div>
                                            </form>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}

                        {/* ══════════════════════════════════════════════════════════════ */}
                        {/* ── MODAL: DOCUMENT PREVIEW ── */}
                        {/* ══════════════════════════════════════════════════════════════ */}
                        {previewDoc && (
                            <>
                                <div className="modal-backdrop fade show" style={{ zIndex: 1060 }}></div>
                                <div className="modal fade show d-block" tabIndex="-1" style={{ zIndex: 1065, background: 'rgba(0,0,0,0.65)' }}>
                                    <div className="modal-dialog modal-dialog-centered modal-xl" style={{ height: '90vh' }}>
                                        <div className="modal-content border-0 shadow-lg h-100 d-flex flex-column" style={{ borderRadius: '14px', overflow: 'hidden' }}>
                                            <div className="modal-header bg-dark text-white py-2 px-3 border-0">
                                                <div className="d-flex align-items-center gap-2 text-truncate" style={{ flex: 1 }}>
                                                    <i className={isPdfFile(previewDoc.fileUrl || previewDoc.file, previewDoc.fileType) ? 'fa fa-file-pdf text-danger' : 'fa fa-file-image text-primary'}></i>
                                                    <span className="fw-bold text-truncate" style={{ fontSize: '0.95rem' }}>
                                                        {previewDoc.title}
                                                    </span>
                                                    {(previewDoc.documentNumber || previewDoc.docNumber) && (
                                                        <span className="badge bg-secondary" style={{ fontSize: '0.72rem' }}>
                                                            #{previewDoc.documentNumber || previewDoc.docNumber}
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="d-flex align-items-center gap-2">
                                                    <a
                                                        href={previewDoc.fileUrl || previewDoc.file}
                                                        download={previewDoc.fileOriginalName || previewDoc.title}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="btn btn-sm btn-outline-light"
                                                        title="Download File"
                                                    >
                                                        <i className="fa fa-download me-1"></i> Download
                                                    </a>
                                                    <a
                                                        href={previewDoc.fileUrl || previewDoc.file}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="btn btn-sm btn-outline-light"
                                                        title="Open in new window"
                                                    >
                                                        <i className="fa fa-external-link-alt"></i>
                                                    </a>
                                                    <button
                                                        type="button"
                                                        className="btn-close btn-close-white"
                                                        onClick={() => setPreviewDoc(null)}
                                                    ></button>
                                                </div>
                                            </div>

                                            <div className="modal-body p-0 flex-grow-1 bg-light d-flex align-items-center justify-content-center" style={{ overflow: 'hidden' }}>
                                                {isPdfFile(previewDoc.fileUrl || previewDoc.file, previewDoc.fileType) ? (
                                                    <iframe
                                                        src={`${previewDoc.fileUrl || previewDoc.file}#toolbar=1&navpanes=0`}
                                                        title={previewDoc.title}
                                                        width="100%"
                                                        height="100%"
                                                        style={{ border: 'none' }}
                                                    />
                                                ) : (
                                                    <div className="p-3 text-center" style={{ overflow: 'auto', maxHeight: '100%', maxWidth: '100%' }}>
                                                        <img
                                                            src={previewDoc.fileUrl || previewDoc.file}
                                                            alt={previewDoc.title}
                                                            style={{ maxWidth: '100%', maxHeight: '75vh', objectFit: 'contain', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}
                                                        />
                                                    </div>
                                                )}
                                            </div>

                                            <div className="modal-footer bg-white py-2 px-3 border-top d-flex justify-content-between">
                                                <div className="small text-muted">
                                                    <span>Category: <strong>{previewDoc.category || 'General'}</strong></span>
                                                    {previewDoc.issueDate && <span className="ms-3">Issued: {formatDate(previewDoc.issueDate)}</span>}
                                                    {previewDoc.expiryDate && <span className="ms-3">Expiry: {formatDate(previewDoc.expiryDate)}</span>}
                                                </div>
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-secondary"
                                                    onClick={() => setPreviewDoc(null)}
                                                >
                                                    Close Preview
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}

                        {/* ══════════════════════════════════════════════════════════════ */}
                        {/* ── MODAL: MANAGE CATEGORIES (ADD / DELETE) ── */}
                        {/* ══════════════════════════════════════════════════════════════ */}
                        {showCategoryModal && (
                            <>
                                <div className="modal-backdrop fade show" style={{ zIndex: 1050 }}></div>
                                <div className="modal fade show d-block" tabIndex="-1" style={{ zIndex: 1055, background: 'rgba(0,0,0,0.5)' }}>
                                    <div className="modal-dialog modal-dialog-centered modal-lg">
                                        <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '14px' }}>
                                            <div className="modal-header border-0 pb-0">
                                                <h5 className="modal-title fw-bold text-dark d-flex align-items-center gap-2">
                                                    <i className="fa fa-tags" style={{ color: 'var(--primary, #0d9488)' }}></i>
                                                    Manage Hospital Categories
                                                </h5>
                                                <button
                                                    type="button"
                                                    className="btn-close"
                                                    onClick={() => setShowCategoryModal(false)}
                                                ></button>
                                            </div>

                                            <div className="modal-body">
                                                <p className="text-muted small mb-3">
                                                    Add new custom categories or delete unwanted ones. Your changes will immediately update the dropdowns and page filters.
                                                </p>

                                                {/* Tabs for Category Type */}
                                                <ul className="nav nav-pills mb-3 gap-2">
                                                    <li className="nav-item">
                                                        <button
                                                            className={`nav-link btn-sm ${activeCategoryTab === 'contact' ? 'active bg-success' : 'text-secondary'}`}
                                                            style={{ borderRadius: '20px', fontWeight: 600, fontSize: '0.85rem' }}
                                                            onClick={() => setActiveCategoryTab('contact')}
                                                        >
                                                            <i className="fa fa-phone me-1"></i> Contact & Shop Categories ({contactCategories.length})
                                                        </button>
                                                    </li>
                                                    <li className="nav-item">
                                                        <button
                                                            className={`nav-link btn-sm ${activeCategoryTab === 'document' ? 'active bg-success' : 'text-secondary'}`}
                                                            style={{ borderRadius: '20px', fontWeight: 600, fontSize: '0.85rem' }}
                                                            onClick={() => setActiveCategoryTab('document')}
                                                        >
                                                            <i className="fa fa-file me-1"></i> Document Categories ({documentCategories.length})
                                                        </button>
                                                    </li>
                                                </ul>

                                                {/* Add New Category Input */}
                                                <form onSubmit={handleAddCategorySubmit} className="mb-4">
                                                    <div className="input-group">
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            placeholder={`Type new ${activeCategoryTab === 'contact' ? 'shop / contact' : 'document'} category name...`}
                                                            value={newCategoryName}
                                                            onChange={(e) => setNewCategoryName(e.target.value)}
                                                        />
                                                        <button
                                                            type="submit"
                                                            className="btn btn-success fw-semibold"
                                                            disabled={isSubmitting || !newCategoryName.trim()}
                                                        >
                                                            <i className="fa fa-plus me-1"></i> Add Category
                                                        </button>
                                                    </div>
                                                </form>

                                                {/* Category List */}
                                                <h6 className="fw-bold small text-muted text-uppercase mb-2">
                                                    Current {activeCategoryTab === 'contact' ? 'Contact' : 'Document'} Categories
                                                </h6>
                                                <div className="list-group" style={{ maxHeight: '280px', overflowY: 'auto' }}>
                                                    {(activeCategoryTab === 'contact' ? contactCategories : documentCategories).map(cat => {
                                                        const catName = typeof cat === 'string' ? cat : cat.name;
                                                        const count = activeCategoryTab === 'contact'
                                                            ? contacts.filter(c => c.category === catName).length
                                                            : documents.filter(d => d.category === catName).length;

                                                        return (
                                                            <div
                                                                key={cat.id || catName}
                                                                className="list-group-item d-flex justify-content-between align-items-center py-2 px-3 border rounded mb-1"
                                                            >
                                                                <div className="d-flex align-items-center gap-2">
                                                                    <i className="fa fa-folder text-muted" style={{ fontSize: '0.85rem' }}></i>
                                                                    <span className="fw-semibold text-dark">{catName}</span>
                                                                    {count > 0 && (
                                                                        <span className="badge bg-light text-secondary border" style={{ fontSize: '0.72rem' }}>
                                                                            {count} {activeCategoryTab === 'contact' ? 'contacts' : 'docs'}
                                                                        </span>
                                                                    )}
                                                                </div>

                                                                {canManage && (
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-sm btn-outline-danger py-0 px-2"
                                                                        title="Delete this category"
                                                                        onClick={() => setDeleteModal({
                                                                            show: true,
                                                                            type: 'category',
                                                                            id: cat.id,
                                                                            name: catName
                                                                        })}
                                                                    >
                                                                        <i className="fa fa-trash" style={{ fontSize: '0.75rem' }}></i>
                                                                    </button>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>

                                            <div className="modal-footer border-0 pt-0">
                                                <button
                                                    type="button"
                                                    className="btn btn-secondary"
                                                    onClick={() => setShowCategoryModal(false)}
                                                >
                                                    Done
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}

                        {/* ══════════════════════════════════════════════════════════════ */}
                        {/* ── MODAL: DELETE CONFIRMATION ── */}
                        {/* ══════════════════════════════════════════════════════════════ */}
                        <DeleteConfirmModal
                            show={deleteModal.show}
                            itemName={deleteModal.name}
                            title={
                                deleteModal.type === 'contact'
                                    ? 'Delete Shop / Contact'
                                    : deleteModal.type === 'document'
                                        ? 'Delete Document'
                                        : 'Delete Category'
                            }
                            onClose={() => setDeleteModal({ show: false, type: null, id: null, name: '' })}
                            onConfirm={confirmDelete}
                        />
                    </>
                )}
            </div>
        </div>
    );
}
